import { yupResolver } from "@hookform/resolvers/yup";
import { Col, Row } from "antd";
import dayjs from "dayjs";
import { cloneDeep, isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import entityType from "../../../api/entityType";
import { transformAgentDailyInput } from "../../../store/inventory/inventoryActions";
import {
  clearOperationResult,
  fetchReport,
  resetReportState,
} from "../../../store/report/reportActions";
import PermissionWrapper from "../../navigation/PermissionWrapper";
import ReportPanel from "../../shared/ReportPanel";
import AgencySelectEx from "../../shared/form/AgencySelectEx";
import { getInitialAgency } from "../../shared/form/getInitialAgency";
import CustomCheckbox from "../../shared/form/reactHookForm/CustomCheckbox";
import CustomDatePicker from "../../shared/form/reactHookForm/CustomDatePicker";
import { dateFormat, toTZNeutralFormat } from "../../shared/formatting";
import openNotificationOperationResult from "../../shared/openNotificationOperationResult";
import { ViewInventory } from "../../shared/securityRoleTasks";
import { agenciesListEqual } from "../../shared/sorting";
import AllocatedToAgentSelect from "./AllocatedToAgentSelect";
import { roadAgentDailyStockSchema } from "./reportSchemas";

// Constants
const partialInitialValues = {
  AgencyKey: "",
  TransactionDate: ["", ""],
  NewPageForEachSeries: true,
  IncludeSignOutInSheet: true,
  IncludeLogSheet: true,
  IncludeSalesLog: false,
};

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 14 },
};

const RoadAgentDailyStock = () => {
  // Form
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    watch,
    trigger,
    formState: { isValid, isDirty, defaultValues, isSubmitted },
  } = useForm({
    mode: "onChange",
    defaultValues: partialInitialValues,
    resolver: yupResolver(roadAgentDailyStockSchema),
  });

  const agencyKey = watch("AgencyKey");
  const includeLogSheet = watch("IncludeLogSheet");
  const includeSalesLog = watch("IncludeSalesLog");

  //
  const dispatch = useDispatch();

  //
  const [currentAgencies, setCurrentAgencies] = useState([]);
  const [initialDefaultValues, setInitialDefaultValues] =
    useState(partialInitialValues);

  //
  const agencies = useSelector((state) => state.agency.listCompact);
  const homeAgency = useSelector((state) => state.actor.details.data.AgencyKey);
  const reportOperationResult = useSelector(
    (state) => state.report.operationResult
  );

  useEffect(() => {
    if (
      agencies != null &&
      !isEmpty(agencies) &&
      !agenciesListEqual(currentAgencies, agencies)
    ) {
      setCurrentAgencies(agencies);
      var initialAgency = getInitialAgency(homeAgency, agencies);
      const fullInitialValues = {
        ...partialInitialValues,
        AgencyKey: initialAgency,
        TransactionDate: [
          toTZNeutralFormat(dayjs().startOf("day")),
          toTZNeutralFormat(dayjs().endOf("day")),
        ],
      };
      setInitialDefaultValues(cloneDeep(fullInitialValues));
      reset(fullInitialValues);
      trigger();
    }
  }, [agencies, homeAgency]);

  useEffect(() => {
    openNotificationOperationResult(reportOperationResult, () =>
      dispatch(clearOperationResult())
    );
  }, [reportOperationResult, dispatch]);

  const onSubmitHandler = (values) => {
    dispatch(
      fetchReport(
        transformAgentDailyInput(values),
        entityType.INVENTORY,
        "PrintAgentDaily",
        () => {
          reset(values);
        }
      )
    );
  };

  const handlers = {
    onSubmit: () => handleSubmit(onSubmitHandler)(),
    onRestore: () => {
      reset(initialDefaultValues);
      dispatch(resetReportState());
    },
  };

  const buttons = [
    {
      action: "preview",
      disabled: !isValid,
    },
    { action: "restore", onClick: handlers.onRestore },
  ];

  return (
    <PermissionWrapper entityName="InventoryReports">
      <ReportPanel
        isDirty={isDirty}
        isSubmitted={isSubmitted}
        onSubmit={handlers.onSubmit}
        buttons={buttons}
        taskId={ViewInventory}
      >
        <Row>
          <Col span={16}>
            <AgencySelectEx
              layout={layout}
              formLabel="Agency"
              name="AgencyKey"
              required={true}
              agencies={currentAgencies}
              isLoading={defaultValues.AgencyKey === ""}
              control={control}
              onChange={() => {
                setValue("AllocatedToAgentKey", null);
                trigger();
              }}
            />

            <AllocatedToAgentSelect
              control={control}
              layout={layout}
              required={true}
              agencyKey={agencyKey}
              allowClear={true}
              includeAllOption={false}
              formLabel="Road Agent"
              setValue={setValue}
            />
          </Col>
        </Row>
        <Row>
          <Col span={12} offset={4}>
            <CustomCheckbox
              formLabel=""
              control={control}
              name="IncludeSignOutInSheet"
              style={{ marginBottom: 0 }}
            >
              Generate Daily Stock Sign Out / In Sheet
            </CustomCheckbox>
          </Col>
        </Row>
        <Row>
          <Col offset={4}>
            <CustomCheckbox
              formLabel=""
              control={control}
              name="IncludeLogSheet"
              style={{ marginBottom: 0 }}
            >
              Generate Inventory Log Sheet
            </CustomCheckbox>
          </Col>
          <Col>
            {includeLogSheet && (
              <CustomCheckbox
                formLabel=""
                control={control}
                name="NewPageForEachSeries"
                disabled={!includeLogSheet}
                style={{ marginBottom: 0, marginLeft: 10 }}
              >
                New Page For Each Series
              </CustomCheckbox>
            )}
          </Col>
        </Row>
        <Row>
          <Col offset={4}>
            <CustomCheckbox
              formLabel=""
              control={control}
              name="IncludeSalesLog"
            >
              Generate Agent's Sales Log For:
            </CustomCheckbox>
          </Col>
          <Col span={10}>
            <CustomDatePicker.RangePicker
              formItem={{
                layout: layout,
                label: "",
                required: true,
              }}
              control={control}
              allowClear={true}
              name="TransactionDate"
              format={dateFormat}
              disabled={includeSalesLog}
              disabledDate={(current) =>
                current &&
                (current > dayjs().endOf("day") ||
                  current <= dayjs().subtract(1, "month").startOf("day"))
              }
            />
          </Col>
        </Row>
      </ReportPanel>
    </PermissionWrapper>
  );
};
export default RoadAgentDailyStock;
